import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Theme as CarbonTheme,
  Pagination,
  PaginationProps,
  Tile,
  ToastNotification,
} from '@carbon/react';
import { statusLabel } from '../lib/format';
import { PAGE_SIZES } from '../lib/pagination';
import { getSavedTheme, getSystemTheme } from '../lib/theme';

/* ---------- status pill ---------- */

export function StatusPill({ status }: { status: string }) {
  return <span className={`pill pill-${status}`}>{statusLabel(status)}</span>;
}

/* ---------- stat tile ---------- */

export function Stat({
  label,
  value,
  meta,
  tint,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tint?: string;
}) {
  return (
    <Tile className="stat" style={tint ? ({ ['--tint' as string]: tint } as React.CSSProperties) : undefined}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {meta ? <div className="stat-meta">{meta}</div> : null}
    </Tile>
  );
}

/* ---------- card ---------- */

export function Card({
  title,
  actions,
  children,
  bodyless,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Skip the padded body wrapper, for cards whose content is a full-bleed table. */
  bodyless?: boolean;
}) {
  return (
    <Tile className="card">
      {title || actions ? (
        <header className="card-head">
          <div className="card-title">{title}</div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      {bodyless ? <div className="card-body-full">{children}</div> : <div className="card-body">{children}</div>}
    </Tile>
  );
}

/* ---------- table pagination ---------- */

/**
 * The paging footer every table uses. It stays hidden until the list outgrows the smallest page
 * size: hiding it whenever the list fits the current page would pull the size selector away the
 * moment someone picks a larger size, leaving no way back.
 */
export function TablePagination({
  page,
  pageSize,
  totalItems,
  onChange,
  labels,
}: {
  /** Zero-based. */
  page: number;
  pageSize: number;
  totalItems: number;
  onChange: (page: number, pageSize: number) => void;
  /** Control names, needed when one screen has more than one paged table so each can be told apart. */
  labels?: Pick<
    PaginationProps,
    'backwardText' | 'forwardText' | 'itemsPerPageText' | 'pageSelectLabelText'
  >;
}) {
  if (totalItems <= Math.min(...PAGE_SIZES)) return null;
  return (
    <Pagination
      {...labels}
      className="table-pagination"
      page={page + 1}
      pageSize={pageSize}
      pageSizes={PAGE_SIZES}
      totalItems={totalItems}
      onChange={({ page: nextPage, pageSize: nextPageSize }) => {
        // A new page size starts again from the first page; the old offset means nothing at the new size.
        onChange(nextPageSize === pageSize ? nextPage - 1 : 0, nextPageSize);
      }}
    />
  );
}

/* ---------- modal ---------- */

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`modal${wide ? ' modal-wide' : ''}`} role="dialog" aria-modal="true">
        <header className="modal-head">
          <div className="modal-title">{title}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

/* ---------- form field ---------- */

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

/* ---------- empty + loading ---------- */

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {children ? <div className="small">{children}</div> : null}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty row" style={{ justifyContent: 'center' }}>
      <span className="spinner" /> <span className="small">{label}…</span>
    </div>
  );
}

/* ---------- toasts ---------- */

type ToastKind = 'ok' | 'error' | 'info';
type CarbonToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

const TOAST_KIND: Record<ToastKind, CarbonToastKind> = {
  ok: 'success',
  error: 'error',
  info: 'info',
};

const TOAST_TITLE: Record<ToastKind, string> = {
  ok: 'Success',
  error: 'Error',
  info: 'Information',
};

const TOAST_TIMEOUT_MS: Record<ToastKind, number> = {
  ok: 4000,
  error: 8000,
  info: 4000,
};

const ToastContext = createContext<(kind: ToastKind, message: string) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

/** Follow the Hub theme attribute / system preference without a second theme store. */
function useResolvedHubTheme(): 'g10' | 'g100' {
  const read = () => ((getSavedTheme() ?? getSystemTheme()) === 'dark' ? 'g100' : 'g10');
  const [theme, setTheme] = useState<'g10' | 'g100'>(read);

  useEffect(() => {
    const sync = () => setTheme(read());
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', sync);
    window.addEventListener('storage', sync);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return theme;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const carbonTheme = useResolvedHubTheme();

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, kind, message }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <CarbonTheme theme={carbonTheme} className="toast-stack">
        {toasts.map((toast) => (
          <ToastNotification
            key={toast.id}
            kind={TOAST_KIND[toast.kind]}
            title={TOAST_TITLE[toast.kind]}
            subtitle={toast.message}
            timeout={TOAST_TIMEOUT_MS[toast.kind]}
            lowContrast
            role={toast.kind === 'error' ? 'alert' : 'status'}
            onClose={() => {
              dismiss(toast.id);
            }}
          />
        ))}
      </CarbonTheme>
    </ToastContext.Provider>
  );
}

/* ---------- stacked bar chart ---------- */

export function VolumeChart({
  data,
}: {
  data: Array<{ day: string; sent: number; failed: number; other: number }>;
}) {
  if (data.length === 0) {
    return <Empty title="No submissions in the last 30 days" />;
  }

  const max = Math.max(...data.map((point) => point.sent + point.failed + point.other), 1);

  return (
    <div>
      <div className="chart">
        {data.map((point) => {
          const total = point.sent + point.failed + point.other;
          return (
            <div
              key={point.day}
              className="chart-col"
              style={{ height: `${(total / max) * 100}%` }}
              title={`${point.day}: ${point.sent} delivered, ${point.failed} failed, ${point.other} pending`}
            >
              {point.sent > 0 ? (
                <div className="chart-seg chart-seg-sent" style={{ height: `${(point.sent / total) * 100}%` }} />
              ) : null}
              {point.other > 0 ? (
                <div className="chart-seg chart-seg-other" style={{ height: `${(point.other / total) * 100}%` }} />
              ) : null}
              {point.failed > 0 ? (
                <div className="chart-seg chart-seg-failed" style={{ height: `${(point.failed / total) * 100}%` }} />
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="legend" style={{ marginTop: 14 }}>
        <span className="legend-item">
          <span className="legend-swatch" style={{ background: 'var(--ok)' }} /> Delivered
        </span>
        <span className="legend-item">
          <span className="legend-swatch" style={{ background: 'var(--info)' }} /> Pending
        </span>
        <span className="legend-item">
          <span className="legend-swatch" style={{ background: 'var(--danger)' }} /> Failed
        </span>
      </div>
    </div>
  );
}
